import React from 'react';
import { ArrowRightIcon, CheckIcon } from 'lucide-react';
import { SmartLink } from '../SmartLink';
import { MockCardView } from '../product/mock/MockCardView';
import { DevicesComposite } from '../product/mock/DevicesComposite';
import { productCatalog } from '../../data/products/shared';
import type { ReleaseEntry, ReleaseTag } from '../../types/changelog';
const tagStyles: Record<ReleaseTag, string> = {
  New: 'bg-brand-soft text-brand',
  Improved: 'bg-[#E9F6EC] text-[#1B7A3D]',
  Fixed: 'bg-[#FBF1E3] text-[#9A5B15]'
};
function productHref(label: string): string {
  return Object.values(productCatalog).find(p => p.title === label)?.href ?? '/';
}
export function ReleaseEntryView({
  entry
}: {
  entry: ReleaseEntry;
}) {
  const primary = entry.products[0];
  const featured = entry.featured;
  return <article aria-labelledby={`release-${entry.id}`} className={`relative ${featured ? 'rounded-[14px] border border-canvas-line bg-white p-6 sm:p-8' : ''}`} data-id="element-1697" data-mp="3ndu7u58kzyc8">
      <span aria-hidden="true" className={`absolute hidden h-[9px] w-[9px] rounded-full ring-4 ring-white lg:block ${featured ? 'left-[-46px] top-9 bg-brand' : 'left-[-45px] top-2 bg-ink/30'}`} data-id="element-1698" data-mp="2k7uauw6of2k2" />

      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[13px]" data-id="element-1699" data-mp="2u731bf2c1z1o">
        <span className={`rounded-full px-2.5 py-1 text-[12px] font-semibold ${tagStyles[entry.tag]}`} data-id="element-1700" data-mp="2z3tjc7xp74pq">{entry.tag}</span>
        <span className="font-medium text-ink" data-id="element-1701" data-mp="21inrd6y266x7">{featured ? 'Early Access' : primary}</span>
        <span aria-hidden="true" className="text-ink-muted" data-id="element-1702" data-mp="26gz3jobtstil">
          ·
        </span>
        <time className="text-ink-muted" data-id="element-1703" data-mp="2be8y1ie2nvda">{entry.date}</time>
      </div>

      <h3 id={`release-${entry.id}`} className={`mt-4 font-semibold tracking-[-0.02em] text-ink ${featured ? 'display text-[28px] sm:text-[34px]' : 'text-[20px]'}`} data-id="element-1704" data-mp="2g5qr7k9usrnp">
        {entry.title}
      </h3>
      <p className={`mt-3 max-w-[640px] leading-[1.6] text-ink-muted ${featured ? 'text-[16px]' : 'text-[15px]'}`} data-id="element-1705" data-mp="1noo3014n97nn">
        {entry.description}
      </p>

      {entry.highlights && <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2" data-id="element-1706" data-mp="1aqgmd0nhvt0n">
          {entry.highlights.map(h => <li key={h} className="flex items-start gap-3 text-[15px] leading-[1.5] text-ink-soft" data-id="element-1707" data-mp="1pdrvkvw7f0xl">
              <span className="mt-[3px] flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-brand-soft" data-id="element-1708" data-mp="1kgrochnmbs4w">
                <CheckIcon className="h-3 w-3 text-brand" strokeWidth={2.5} aria-hidden="true" data-id="element-1709" data-mp="wqe7zoeebhq5" />
              </span>
              {h}
            </li>)}
        </ul>}

      {entry.visual && <div role="img" aria-label={`Screenshot: ${entry.title}`} className={`mt-6 flex items-center justify-center rounded-[12px] bg-canvas-lilac ${featured ? 'px-4 py-8 sm:px-10 sm:py-12' : 'max-w-[560px] p-6 sm:p-8'}`} data-id="element-1710" data-mp="16m8vawtke0z9">
          {entry.visual.kind === 'devices' ? <DevicesComposite tablet={entry.visual.tablet} phone={entry.visual.phone} data-id="element-1711" data-mp="2kndmmrprcaqf" /> : <div className="w-full max-w-[360px]" data-id="element-1712" data-mp="2pnj8fr9cj30l">
              <MockCardView card={entry.visual.card} data-id="element-1713" data-mp="2pob04qztogll" />
            </div>}
        </div>}

      <SmartLink href={productHref(primary)} className="group mt-5 inline-flex items-center gap-1.5 text-[15px] font-medium text-brand transition-colors duration-150 ease-out hover:text-brand-hover" data-id="element-1714" data-mp="2bpbfrvjcenv8">
        Learn more<span className="sr-only" data-id="element-1715" data-mp="38d7v6x31nd4v"> about {entry.title}</span>
        <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-1716" data-mp="38flqn71myrcy" />
      </SmartLink>
    </article>;
}